// Auth mutations (PRD §9.4 + docs/frontend_architecture.md §5 + §6).
//
// TanStack Query wrappers around the helpers in api/auth.ts. The helpers
// already publish to the in-memory authStore on success and clear it on
// logout — these hooks add cache-lifecycle concerns (clearing cached
// server data on a session boundary) and a consistent loading/error
// surface for the form and the logout button.
//
// Navigation lives in the *callers* (the login form needs to honor the
// `redirect` search param; the logout button just goes to /login), so
// the hooks stay reusable and the side effects are explicit at the call
// site.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { login, logout } from '../auth';
import type { AuthUser } from '../../stores/authStore';

interface LoginArgs {
  email: string;
  password: string;
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation<AuthUser, Error, LoginArgs>({
    mutationFn: ({ email, password }) => login(email, password),
    onSuccess: () => {
      // Drop any anonymous prefetches or stale data from a prior session
      // before the new user's queries start populating the cache.
      queryClient.clear();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => logout(),
    // Clear regardless of server-call outcome — the local store is
    // already wiped by auth.logout()'s finally block, so the cache must
    // not retain queries scoped to the old session.
    onSettled: () => {
      queryClient.clear();
    },
  });
}
